import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { TimberKit, ProposalNodeSet, InstallNodeData, TravelNodeData, createDefaultNodeSet } from '../../types';
import { ProposalRateSettings } from '../../utils/appSettings';
import { getInstallLaborLines } from '../../utils/proposalInstallLabor';
import { computeTravelNodeRawTotal, withDefaultHotelNights } from '../../utils/proposalTravelTotals';
import InstallInputs from './InstallInputs';
import TravelInputs from './TravelInputs';
import { Package, ChevronRight, ChevronLeft, Check, Loader2, DollarSign, Trees, Truck } from 'lucide-react';
import './KitProposalWizard.css';

interface KitProposalWizardProps {
    dealId: string;
    rates: ProposalRateSettings | null;
    onComplete: (nodeSets: ProposalNodeSet[], selectedSpecies: string[], selectedKit: TimberKit) => void;
    onCancel: () => void;
    destinationCity?: string; // For travel distance calculation
}

type WizardStep = 1 | 2 | 3;

const KitProposalWizard: React.FC<KitProposalWizardProps> = ({
    dealId,
    rates,
    onComplete,
    onCancel,
    destinationCity
}) => {
    // Step state
    const [currentStep, setCurrentStep] = useState<WizardStep>(1);

    // Kit selection state
    const [kits, setKits] = useState<TimberKit[]>([]);
    const [loadingKits, setLoadingKits] = useState(true);
    const [selectedKitId, setSelectedKitId] = useState<string | null>(null);

    // Species selection state - now supports multiple species
    const [selectedSpecies, setSelectedSpecies] = useState<string[]>([]);

    // Install & Travel state - create working node sets (one per structure)
    const [workingNodeSets, setWorkingNodeSets] = useState<ProposalNodeSet[]>([]);

    // Load active kits
    useEffect(() => {
        // Helper to safely convert timestamps
        const convertTimestamp = (timestamp: any): Date | undefined => {
            if (!timestamp) return undefined;
            // Firestore Timestamp with toDate method
            if (timestamp.toDate && typeof timestamp.toDate === 'function') {
                return timestamp.toDate();
            }
            // Already a Date object
            if (timestamp instanceof Date) {
                return timestamp;
            }
            // ISO string or number
            if (typeof timestamp === 'string' || typeof timestamp === 'number') {
                const parsed = new Date(timestamp);
                return isNaN(parsed.getTime()) ? undefined : parsed;
            }
            // Firestore-like object {seconds, nanoseconds}
            if (timestamp.seconds !== undefined) {
                return new Date(timestamp.seconds * 1000);
            }
            return undefined;
        };

        const q = query(
            collection(db, 'timber_kits'),
            where('status', '==', 'active')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const kitList: TimberKit[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    createdAt: convertTimestamp(data.createdAt) || new Date(),
                    updatedAt: convertTimestamp(data.updatedAt) || new Date(),
                    saleEndsAt: convertTimestamp(data.saleEndsAt),
                } as TimberKit;
            });
            setKits(kitList);
            setLoadingKits(false);
        });

        return () => unsubscribe();
    }, []);

    // Get selected kit
    const selectedKit = kits.find(k => k.id === selectedKitId) || null;

    // Debug: Log selectedKit info
    console.log('[KitWizard] selectedKitId:', selectedKitId, 'selectedKit:', selectedKit?.name, 'nodeSets:', selectedKit?.nodeSets?.length);

    // Copy kit's nodeSets to working state when kit is selected
    // Using selectedKitId AND selectedKit to ensure we catch when kit data loads
    useEffect(() => {
        if (selectedKitId && selectedKit && selectedKit.nodeSets && selectedKit.nodeSets.length > 0) {

            // Skip if we already have the correct number of working nodeSets with user changes
            if (workingNodeSets.length === selectedKit.nodeSets.length &&
                workingNodeSets.some(ns => ns.install.days > 0 || ns.travel.travelDays > 0)) {
                return; // User has made changes, don't reset
            }

            // Clone all nodeSets from kit as our working copies
            const clonedNodeSets = selectedKit.nodeSets.map((ns, index) => ({
                ...ns,
                id: `nodeset-${Date.now()}-${index}`,
            }));
            setWorkingNodeSets(clonedNodeSets);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedKitId, selectedKit?.nodeSets?.length]); // Also trigger when kit's nodeSets become available

    // Calculate price for selected species
    const getSpeciesPrice = (speciesName: string) => {
        if (!selectedKit) return 0;
        const speciesPricing = selectedKit.speciesPricing?.find(sp => sp.speciesName === speciesName);
        if (!speciesPricing) return selectedKit.billableTotal || 0;

        // Apply sale discount if active
        let price = speciesPricing.billableTotal;
        if (selectedKit.isOnSale && selectedKit.saleDiscount) {
            if (selectedKit.saleDiscountType === 'percentage') {
                price = price * (1 - selectedKit.saleDiscount / 100);
            } else {
                price = price - selectedKit.saleDiscount;
            }
        }
        return price;
    };

    // Format currency
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    // Handle step navigation
    const canProceedToStep2 = selectedKitId !== null;
    const canProceedToStep3 = selectedSpecies.length > 0;
    const canComplete = workingNodeSets.length > 0 && workingNodeSets.every(ns => ns.install.days > 0);

    const goToStep = (step: WizardStep) => {
        if (step === 2 && !canProceedToStep2) return;
        if (step === 3 && !canProceedToStep3) return;
        setCurrentStep(step);
    };

    const handleComplete = () => {
        if (!selectedKit || selectedSpecies.length === 0) return;

        // Use working nodeSets directly - they have per-structure install/travel already
        const finalNodeSets: ProposalNodeSet[] = workingNodeSets.map((ns, index) => ({
            ...ns,
            id: `nodeset-${Date.now()}-${index}`,
        }));

        onComplete(finalNodeSets, selectedSpecies, selectedKit);
    };

    // Handle install update for a specific structure
    const handleInstallUpdate = (index: number, installData: InstallNodeData) => {
        setWorkingNodeSets(prev => prev.map((ns, i) => {
            if (i !== index) return ns;
            const travelBase = withDefaultHotelNights(
                ns.travel,
                installData.days,
                ns.install.days,
                ns.travel.travelDays,
            );
            const travel = rates
                ? {
                    ...travelBase,
                    total: computeTravelNodeRawTotal(travelBase, rates, getInstallLaborLines(installData)),
                }
                : travelBase;
            return { ...ns, install: installData, travel };
        }));
    };

    // Handle travel update for a specific structure
    const handleTravelUpdate = (index: number, travelData: TravelNodeData) => {
        setWorkingNodeSets(prev => prev.map((ns, i) =>
            i === index ? { ...ns, travel: travelData } : ns
        ));
    };

    return (
        <div className="kit-wizard-overlay">
            <div className="kit-wizard-modal">
                {/* Header with progress */}
                <div className="kit-wizard-header">
                    <h2><Package size={24} /> Create Kit Proposal</h2>
                    <div className="kit-wizard-progress">
                        <div
                            className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}
                            onClick={() => goToStep(1)}
                        >
                            <span className="step-number">1</span>
                            <span className="step-label">Select Kit</span>
                        </div>
                        <div className="progress-connector" />
                        <div
                            className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}
                            onClick={() => canProceedToStep2 && goToStep(2)}
                        >
                            <span className="step-number">2</span>
                            <span className="step-label">Species</span>
                        </div>
                        <div className="progress-connector" />
                        <div
                            className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}
                            onClick={() => canProceedToStep3 && goToStep(3)}
                        >
                            <span className="step-number">3</span>
                            <span className="step-label">Install & Travel</span>
                        </div>
                    </div>
                </div>

                {/* Step Content */}
                <div className="kit-wizard-body">
                    {/* Step 1: Kit Selection */}
                    {currentStep === 1 && (
                        <div className="wizard-step step-1">
                            <h3><Package size={20} /> Select a Kit</h3>
                            <p className="step-description">Choose the timber frame kit for this proposal</p>

                            {loadingKits ? (
                                <div className="wizard-loading">
                                    <Loader2 size={32} className="spin" />
                                    <p>Loading kits...</p>
                                </div>
                            ) : kits.length === 0 ? (
                                <div className="wizard-empty">
                                    <Package size={48} strokeWidth={1} />
                                    <p>No active kits available</p>
                                    <p className="empty-hint">Create kits in Kit Management first</p>
                                </div>
                            ) : (
                                <div className="kit-selection-grid">
                                    {kits.map(kit => (
                                        <div
                                            key={kit.id}
                                            className={`kit-selection-card ${selectedKitId === kit.id ? 'selected' : ''} ${kit.isOnSale ? 'on-sale' : ''}`}
                                            onClick={() => setSelectedKitId(kit.id)}
                                        >
                                            <div className="kit-card-image">
                                                {kit.thumbnailUrl ? (
                                                    <img src={kit.thumbnailUrl} alt={kit.name} />
                                                ) : (
                                                    <div className="kit-card-placeholder">
                                                        <Package size={40} strokeWidth={1} />
                                                    </div>
                                                )}
                                                {kit.isOnSale && (
                                                    <div className="sale-badge">
                                                        {kit.saleDiscountType === 'percentage'
                                                            ? `${kit.saleDiscount}% OFF`
                                                            : `$${kit.saleDiscount} OFF`}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="kit-card-info">
                                                <h4>{kit.name}</h4>
                                                {kit.description && (
                                                    <p className="kit-description">{kit.description}</p>
                                                )}
                                                <div className="kit-price-range">
                                                    <DollarSign size={14} />
                                                    {kit.speciesPricing && kit.speciesPricing.length > 0 ? (
                                                        <span>
                                                            {formatCurrency(Math.min(...kit.speciesPricing.map(sp => sp.billableTotal)))} - {formatCurrency(Math.max(...kit.speciesPricing.map(sp => sp.billableTotal)))}
                                                        </span>
                                                    ) : (
                                                        <span>{formatCurrency(kit.billableTotal || 0)}</span>
                                                    )}
                                                </div>
                                            </div>
                                            {selectedKitId === kit.id && (
                                                <div className="selection-check">
                                                    <Check size={18} />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 2: Species Selection - Multi-Select */}
                    {currentStep === 2 && selectedKit && (
                        <div className="wizard-step step-2">
                            <h3><Trees size={20} /> Select Species to Offer</h3>
                            <p className="step-description">Choose which timber species options to include in this proposal</p>

                            {selectedKit.speciesPricing && selectedKit.speciesPricing.length > 0 && (
                                <div className="species-select-all">
                                    <button
                                        className="select-all-btn"
                                        onClick={() => {
                                            const allSpecies = selectedKit.speciesPricing
                                                .filter(sp => sp.speciesName !== 'Default')
                                                .map(sp => sp.speciesName);
                                            setSelectedSpecies(allSpecies);
                                        }}
                                    >
                                        Select All
                                    </button>
                                    <button
                                        className="clear-all-btn"
                                        onClick={() => setSelectedSpecies([])}
                                    >
                                        Clear
                                    </button>
                                    <span className="selection-count">
                                        {selectedSpecies.length} of {selectedKit.speciesPricing.filter(sp => sp.speciesName !== 'Default').length} selected
                                    </span>
                                </div>
                            )}

                            <div className="species-selection-list">
                                {selectedKit.speciesPricing && selectedKit.speciesPricing.length > 0 ? (
                                    selectedKit.speciesPricing
                                        .filter(sp => sp.speciesName !== 'Default')
                                        .map(sp => {
                                            const finalPrice = getSpeciesPrice(sp.speciesName);
                                            const hasDiscount = selectedKit.isOnSale && selectedKit.saleDiscount;
                                            const isSelected = selectedSpecies.includes(sp.speciesName);

                                            const toggleSpecies = () => {
                                                if (isSelected) {
                                                    setSelectedSpecies(prev => prev.filter(s => s !== sp.speciesName));
                                                } else {
                                                    setSelectedSpecies(prev => [...prev, sp.speciesName]);
                                                }
                                            };

                                            return (
                                                <div
                                                    key={sp.speciesName}
                                                    className={`species-option ${isSelected ? 'selected' : ''}`}
                                                    onClick={toggleSpecies}
                                                >
                                                    <div className="species-checkbox">
                                                        {isSelected ? (
                                                            <div className="checkbox-checked">✓</div>
                                                        ) : (
                                                            <div className="checkbox-unchecked" />
                                                        )}
                                                    </div>
                                                    <div className="species-info">
                                                        <span className="species-name">{sp.speciesName}</span>
                                                        <div className="species-price">
                                                            {hasDiscount && (
                                                                <span className="original-price">{formatCurrency(sp.billableTotal)}</span>
                                                            )}
                                                            <span className={`final-price ${hasDiscount ? 'sale' : ''}`}>
                                                                {formatCurrency(finalPrice)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                ) : (
                                    <div className="wizard-empty">
                                        <Trees size={48} strokeWidth={1} />
                                        <p>No species pricing configured for this kit</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Step 3: Install & Travel */}
                    {currentStep === 3 && rates && (
                        <div className="wizard-step step-3">
                            <h3><Truck size={20} /> Configure Installation</h3>
                            <p className="step-description">
                                Set up installation and travel for {workingNodeSets.length > 1 ? 'each structure' : 'your structure'}
                            </p>

                            {workingNodeSets.map((ns, index) => (
                                <div key={ns.id} className="structure-install-section">
                                    {workingNodeSets.length > 1 && (
                                        <h4 className="structure-header">{ns.name}</h4>
                                    )}
                                    <div className="install-travel-grid">
                                        <div className="config-section">
                                            <h4>Installation</h4>
                                            <InstallInputs
                                                data={ns.install}
                                                rates={rates}
                                                onUpdate={(data) => handleInstallUpdate(index, data)}
                                            />
                                        </div>

                                        <div className="config-section">
                                            <h4>Travel</h4>
                                            <TravelInputs
                                                data={ns.travel}
                                                rates={rates}
                                                installDays={ns.install.days}
                                                laborLines={getInstallLaborLines(ns.install)}
                                                onUpdate={(data) => handleTravelUpdate(index, data)}
                                                destinationCity={destinationCity}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer with navigation */}
                <div className="kit-wizard-footer">
                    <button className="wizard-btn secondary" onClick={onCancel}>
                        Cancel
                    </button>

                    <div className="wizard-nav">
                        {currentStep > 1 && (
                            <button
                                className="wizard-btn secondary"
                                onClick={() => setCurrentStep((currentStep - 1) as WizardStep)}
                            >
                                <ChevronLeft size={18} /> Back
                            </button>
                        )}

                        {currentStep < 3 ? (
                            <button
                                className="wizard-btn primary"
                                onClick={() => setCurrentStep((currentStep + 1) as WizardStep)}
                                disabled={
                                    (currentStep === 1 && !canProceedToStep2) ||
                                    (currentStep === 2 && !canProceedToStep3)
                                }
                            >
                                Next <ChevronRight size={18} />
                            </button>
                        ) : (
                            <button
                                className="wizard-btn primary complete"
                                onClick={handleComplete}
                                disabled={!canComplete}
                            >
                                <Check size={18} /> Create Proposal
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KitProposalWizard;
